import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { GraduationCap, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err, "Could not reset password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="px-8 lg:px-16 py-8">
        <Link to="/" className="flex items-center gap-3 w-fit">
          <div className="w-9 h-9 bg-[#E25822] flex items-center justify-center">
            <GraduationCap size={22} weight="bold" color="#FFFFFF" />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">UzviS Learning</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 -mt-16">
        <div className="max-w-md w-full page-fade">
          {!token ? (
            <div data-testid="reset-missing-token">
              <WarningCircle size={42} weight="duotone" color="#993333" className="mb-5" />
              <div className="eyebrow mb-3">Missing token</div>
              <h1 className="font-heading text-4xl font-semibold tracking-tight leading-none mb-3">
                Invalid reset link.
              </h1>
              <p className="text-[#5C5C58] mb-8">
                This page requires a reset token. Use the link from your reset email, or request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center px-6 py-3 bg-[#E25822] hover:bg-[#C44E1A] text-white font-medium"
              >
                Request a new link
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <CheckCircle size={42} weight="duotone" color="#4A6B53" className="mb-5" />
              <div className="eyebrow mb-3">All set</div>
              <h1 className="font-heading text-4xl font-semibold tracking-tight leading-none mb-3">
                Password updated.
              </h1>
              <p className="text-[#5C5C58] mb-8">Redirecting you to the sign-in page...</p>
              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 bg-[#E25822] hover:bg-[#C44E1A] text-white font-medium"
              >
                Sign in now
              </Link>
            </div>
          ) : (
            <>
              <div className="eyebrow mb-3">Account recovery</div>
              <h1 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight leading-none mb-3">
                Set a new password.
              </h1>
              <p className="text-[#5C5C58] mb-10">Choose a password of at least 6 characters.</p>

              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#3D3D38]">
                    New password
                  </Label>
                  <Input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 h-12 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
                    placeholder="At least 6 characters"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#3D3D38]">
                    Confirm password
                  </Label>
                  <Input
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-2 h-12 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
                    placeholder="Type it again"
                    data-testid="reset-confirm-input"
                  />
                </div>
                {error && (
                  <div
                    className="text-sm text-[#993333] border-l-2 border-[#993333] pl-3 py-1 bg-red-50"
                    data-testid="reset-error"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[#E25822] hover:bg-[#C44E1A] text-white rounded-lg text-base font-medium"
                  data-testid="reset-submit-button"
                >
                  {submitting ? "Updating..." : "Reset password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
